import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Card, 
    CardContent, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    CircularProgress,
    TextField,
    Button,
    Grid
} from '@mui/material';
import { api } from 'services/api';
import { formatNumber } from '../../utils/formatters';

interface NonCustodialExchangeAnalytics {
    assetCode: string;
    assetName: string;
    totalExchangeAmount: string;
    brokerCommission: string;
    exchangeRemainder: string;
    brokerWithdrawCommission: string;
    totalCommission: string;
}

interface ApiResponse {
    success: boolean;
    data: {
        analytics: NonCustodialExchangeAnalytics[];
        filter: {
            date: string | null;
            assetCode: string | null;
            description: string;
        };
    };
}

const NonCustodialExchangesPage: React.FC = () => {
    const [analytics, setAnalytics] = useState<NonCustodialExchangeAnalytics[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filterDate, setFilterDate] = useState<string>('');
    const [filterAsset, setFilterAsset] = useState<string>('');
    const [filterInfo, setFilterInfo] = useState<string>('All time data');

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            
            if (filterDate) {
                params.append('date', filterDate);
            }
            if (filterAsset) {
                params.append('assetCode', filterAsset);
            }
            
            const response = await api.get<ApiResponse>(
                `/api/v1/admin-panel/exchanges/non-custody/analytics?${params.toString()}`
            );
            
            setAnalytics(response.data.data.analytics);
            setFilterInfo(response.data.data.filter.description);
            setError(null);
        } catch (err) {
            setError('Failed to load non-custodial exchange analytics');
            console.error('Error fetching analytics:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const handleFilter = () => {
        fetchAnalytics();
    };

    const handleClearFilter = () => {
        setFilterDate('');
        setFilterAsset('');
        setTimeout(() => fetchAnalytics(), 100);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                Аналитика некастодиальных обменов
            </Typography>

            {/* Filter Controls */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Фильтры
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Дата"
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                helperText="Показать данные за конкретный день"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Код актива"
                                value={filterAsset}
                                onChange={(e) => setFilterAsset(e.target.value.toUpperCase())}
                                placeholder="BTC, ETH, USDT..."
                                fullWidth
                                size="small"
                                helperText="Фильтр по активу"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button 
                                variant="contained" 
                                onClick={handleFilter}
                                fullWidth
                            >
                                Применить фильтр
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button 
                                variant="outlined" 
                                onClick={handleClearFilter}
                                fullWidth
                            >
                                Показать все
                            </Button>
                        </Grid>
                    </Grid>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        <strong>Данные:</strong> {filterInfo}
                    </Typography>
                </CardContent>
            </Card>

            {/* Analytics Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Актив</TableCell>
                            <TableCell align="right">Сумма обменов</TableCell>
                            <TableCell align="right">Комиссия брокера</TableCell>
                            <TableCell align="right">Остаток обмена</TableCell>
                            <TableCell align="right">Комиссия вывода</TableCell>
                            <TableCell align="right">Общая комиссия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {analytics.map((item) => (
                            <TableRow key={item.assetCode}>
                                <TableCell>
                                    <strong>{item.assetCode}</strong>
                                    <br />
                                    <Typography variant="caption" color="textSecondary">
                                        {item.assetName}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(Number(item.totalExchangeAmount))}
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(Number(item.brokerCommission))}
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(Number(item.exchangeRemainder))}
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(Number(item.brokerWithdrawCommission))}
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatNumber(Number(item.totalCommission))}</strong>
                                </TableCell>
                            </TableRow>
                        ))}
                        {analytics.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        Нет данных для отображения
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default NonCustodialExchangesPage;